/**
 * inboundDatevRefs — reine §7-Filterlogik für den Eingangsbelege-DATEV-Belegtransfer
 * (IDV-6, architecture-inbound-datev.md §5.5/§7).
 *
 * Verantwortung: aus einer bereits geladenen Liste von `inbound_documents` (FE,
 * owner-gescopt) diejenigen auswählen, die in ein DATEV-Belegtransfer-Paket (ZIP)
 * aufgenommen werden dürfen, und daraus den Beleg-Deskriptor bauen, den
 * `functions/src/datev/buildDatevBelegePaket.ts` erwartet (dort `BelegRefInput`
 * genannt — hier `InboundBelegRefInput`, Shape byte-identisch für die
 * `inbound_documents`-Quelle: `collection`/`docId`/`fileKind`/`guid`/`filenameInZip`).
 *
 * VOLLSTÄNDIG PUR (D-2, §5.5): kein Firestore-Zugriff, kein `Math.random`/`Date.now`
 * als Entscheidungsgrundlage — dieselbe `docs`-Liste + dieselben `opts` liefern
 * IMMER dasselbe Ergebnis. Der server-seitige Export-Marker-Write (nach
 * `archive.finalize()`) passiert ausschließlich in der Function (IDV-5) — dieses
 * Modul liest den Marker (`datevExportedAt`) nur, es schreibt ihn nie.
 *
 * GUID-VERTRAG: `deriveBelegGuid` (aus `./datevBelege`) MUSS bit-identisch zur
 * Function bleiben — `documentKey = 'inbound_documents:' + docId` (collection
 * bereits kleingeschrieben). NICHT ändern ohne Server-Sync (s. `datevBelege.ts`-
 * Kopfkommentar „GUID-VERTRAG (HART GEPINNT)").
 *
 * @module inboundDatevRefs
 */

import type { InboundDocument, InboundFormat } from '../types/inboundTypes';
import { deriveBelegGuid } from './datevBelege';

/**
 * Beleg-Deskriptor für `inbound_documents` — Shape MUSS exakt dem `BelegRefInput`-
 * Vertrag der Function entsprechen (`functions/src/datev/buildDatevBelegePaket.ts`).
 * `collection`/`fileKind` sind für diese Quelle konstant, da inbound-Belege
 * serverseitig immer als Original-Datei behandelt werden (kein pdf/xml/receipt-
 * Split wie bei `invoices`/`expenses`, s. §5.3).
 */
export interface InboundBelegRefInput {
  collection: 'inbound_documents';
  docId: string;
  fileKind: 'original';
  guid: string;
  filenameInZip: string;
}

/** Optionen für {@link buildInboundDatevRefs}. */
export interface BuildInboundDatevRefsOptions {
  /**
   * Nimmt auch bereits per Marker (`datevExportedAt`) übergebene Belege wieder auf
   * (D-2 Rückhol-Toggle, §5.6/GL-3). Default `false` — fail-closed kein
   * versehentlicher Doppel-Beleg im StB-Import.
   */
  includeAlreadyExported?: boolean;
}

/**
 * §7-exportierbare Formate: Belegbild-fähig (`zugferd_pdf`/`pdf`/`image`) ODER
 * reine XML (`xrechnung_cii`/`xrechnung_ubl` — Portabilität I-7; GL-1-Realtest vor
 * Flag-on offen). `xml_unparseable`/`other` sind NICHT enthalten (kein sinnvoller
 * Beleg, §7 letzter Punkt).
 */
const EXPORTABLE_FORMATS: ReadonlySet<InboundFormat> = new Set<InboundFormat>([
  'zugferd_pdf',
  'pdf',
  'image',
  'xrechnung_cii',
  'xrechnung_ubl',
]);

/**
 * `true`, wenn ein Dokument als „XML-Beleg (keine Bildvorschau)" markiert werden
 * soll (§7) — reine UI-Klassifikation für den Aufrufer (z. B.
 * `InboundDatevExportSection`); ändert NICHT die an den Server gesendeten Felder
 * (`fileKind` bleibt für jeden inbound-Ref `'original'`).
 *
 * @param doc Inbound-Dokument (nur `format` wird gelesen).
 * @returns `true` bei `xrechnung_cii`/`xrechnung_ubl`.
 */
export function isInboundXmlOnly(doc: Pick<InboundDocument, 'format'>): boolean {
  return doc.format === 'xrechnung_cii' || doc.format === 'xrechnung_ubl';
}

/** Reduziert einen Namens-Bestandteil auf ZIP-sichere Zeichen (Muster `datevExport.ts`). */
function sanitizeSegment(value: string): string {
  const cleaned = (value ?? '').replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return cleaned.length > 0 ? cleaned : 'beleg';
}

/**
 * Ermittelt eine plausible Dateiendung: bevorzugt die reale Endung aus
 * `doc.filename`, sonst einen Format-Fallback. Rein kosmetisch — der Server bleibt
 * Autorität über den tatsächlichen ZIP-Eintragsnamen (`sanitizeZipEntryName`,
 * `functions/src/datev/buildDatevBelegePaket.ts`).
 */
function extensionForDoc(doc: Pick<InboundDocument, 'filename' | 'format'>): string {
  const fromFilename = /\.([A-Za-z0-9]{1,8})$/.exec(doc.filename ?? '');
  if (fromFilename) return `.${fromFilename[1].toLowerCase()}`;
  if (doc.format === 'zugferd_pdf' || doc.format === 'pdf') return '.pdf';
  if (doc.format === 'xrechnung_cii' || doc.format === 'xrechnung_ubl') return '.xml';
  if (doc.format === 'image') return '.jpg';
  return '.bin';
}

/** Baut den Vorschlags-Dateinamen im ZIP-Root (bloßer Name, kein Verzeichnisanteil). */
function buildFilenameInZip(doc: Pick<InboundDocument, 'id' | 'filename' | 'format'>): string {
  const ext = extensionForDoc(doc);
  const rawBase =
    doc.filename && doc.filename.trim().length > 0
      ? doc.filename.replace(/\.[A-Za-z0-9]{1,8}$/, '')
      : doc.id;
  return `${sanitizeSegment(rawBase)}${ext}`;
}

/**
 * §7-Filter: wählt aus `docs` diejenigen aus, die in den Eingangsbelege-DATEV-
 * Belegtransfer aufgenommen werden dürfen, und baut je Treffer den Beleg-
 * Deskriptor für die Function.
 *
 * Kriterien (ALLE müssen gelten, fail-closed):
 *  - `processingStatus === 'processed'` (kein Stub/deferred/failed),
 *  - `datevExportedAt` leer/null — AUSSER `opts.includeAlreadyExported === true`,
 *  - `format` ist ein exportierbares Format (§7; siehe {@link EXPORTABLE_FORMATS}).
 * `validation.status` ist bewusst KEIN Kriterium — kein Auto-Reject (§5.7,
 * Tester-Property „processed+sichtbar+nicht aus §7-Filter entfernt").
 *
 * Total: wirft nie. Nicht-array-artige/`null`/`undefined`-Eingabe liefert `[]`.
 *
 * @param docs Bereits geladene, owner-gescopte `inbound_documents`.
 * @param opts Optionen (Rückhol-Toggle, s. {@link BuildInboundDatevRefsOptions}).
 * @returns Beleg-Deskriptoren für `downloadInboundDatevPaket`.
 */
export function buildInboundDatevRefs(
  docs: readonly InboundDocument[] | null | undefined,
  opts?: BuildInboundDatevRefsOptions,
): InboundBelegRefInput[] {
  if (!Array.isArray(docs)) return [];
  const includeAlreadyExported = opts?.includeAlreadyExported === true;

  return docs
    .filter((doc): doc is InboundDocument => !!doc)
    .filter((doc) => doc.processingStatus === 'processed')
    .filter((doc) => includeAlreadyExported || doc.datevExportedAt == null)
    .filter((doc) => EXPORTABLE_FORMATS.has(doc.format))
    .map((doc) => {
      const documentKey = `inbound_documents:${doc.id}`;
      return {
        collection: 'inbound_documents' as const,
        docId: doc.id,
        fileKind: 'original' as const,
        guid: deriveBelegGuid(documentKey),
        filenameInZip: buildFilenameInZip(doc),
      };
    });
}
